import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Building2, Mail, UserPlus, ExternalLink, Linkedin, Phone, Copy, Check, X, GripVertical, Settings2, Pencil, Trash2, Loader2, Search, ArrowUpDown } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { getApiError } from '../utils/apiError';
import TagSelect from '../components/TagSelect';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import LogInteractionModal from '../components/LogInteractionModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PipelineConfigModal = ({ stages, onClose, onSaved }) => {
    const [localStages, setLocalStages] = useState(stages);
    const [addStageOpen, setAddStageOpen] = useState(false);
    const [editStage, setEditStage] = useState(null);
    const [deleteStage, setDeleteStage] = useState(null);
    const [leadCount, setLeadCount] = useState(0);
    const [migrateTargetId, setMigrateTargetId] = useState('');
    const [newStage, setNewStage] = useState({ name: '', color: '#94a3b8', category: 'OPEN' });

    useEffect(() => { setLocalStages(stages); }, [stages]);

    const handleReorder = async (result) => {
        if (!result.destination) return;
        const items = Array.from(localStages);
        const [reordered] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reordered);
        setLocalStages(items);
        const promises = items.map((item, i) =>
            axios.patch(`${API_URL}/pipeline/stages/${item.id}`, { order: i })
        );
        const results = await Promise.allSettled(promises);
        const failed = results.some((r) => r.status === 'rejected');
        if (failed) {
            toast.error('Failed to save stage order');
            onSaved();
        } else {
            toast.success('Stages reordered');
            onSaved();
        }
    };

    const handleAddStage = async () => {
        if (!newStage.name.trim()) { toast.error('Stage name is required'); return; }
        try {
            await axios.post(`${API_URL}/pipeline/stages`, newStage);
            toast.success('Stage added');
            setNewStage({ name: '', color: '#94a3b8', category: 'OPEN' });
            setAddStageOpen(false);
            onSaved();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to add stage'));
        }
    };

    const handleSaveEdit = async () => {
        if (!editStage?.name?.trim()) return;
        try {
            await axios.patch(`${API_URL}/pipeline/stages/${editStage.id}`, { name: editStage.name, color: editStage.color, category: editStage.category });
            toast.success('Stage updated');
            setEditStage(null);
            onSaved();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to update stage'));
        }
    };

    const handleDeleteClick = async (stage) => {
        let count;
        try {
            const { data } = await axios.get(`${API_URL}/pipeline/stages/${stage.id}/lead-count`);
            count = data?.count || 0;
        } catch (e) {
            toast.error(getApiError(e, 'Failed to fetch lead count'));
            return;
        }
        if (count > 0) {
            setDeleteStage(stage);
            setLeadCount(count);
            setMigrateTargetId('');
        } else {
            try {
                await axios.delete(`${API_URL}/pipeline/stages/${stage.id}`);
                toast.success('Stage deleted');
                onSaved();
            } catch (e) {
                toast.error(getApiError(e, 'Failed to delete stage'));
            }
        }
    };

    const handleMigrateAndDelete = async () => {
        if (!migrateTargetId || migrateTargetId === deleteStage?.id) {
            toast.error('Select a target stage');
            return;
        }
        try {
            await axios.post(`${API_URL}/pipeline/stages/${deleteStage.id}/migrate-and-delete`, { target_stage_id: migrateTargetId });
            toast.success(`Moved ${leadCount} leads and deleted stage`);
            setDeleteStage(null);
            onSaved();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to migrate and delete'));
        }
    };

    return (
        <div className="mt-4 space-y-4">
            <DragDropContext onDragEnd={handleReorder}>
                <Droppable droppableId="stages-list">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                            {localStages.map((stage, index) => (
                                <Draggable key={stage.id} draggableId={stage.id} index={index}>
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className="flex items-center gap-3 p-3 rounded-lg border bg-white"
                                        >
                                            <div {...provided.dragHandleProps} className="cursor-grab">
                                                <GripVertical className="h-4 w-4 text-slate-400" />
                                            </div>
                                            <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-slate-900">{stage.name}</p>
                                                <p className="text-xs text-slate-500">{stage.category} {stage.is_system && '• System'}</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" variant="ghost" onClick={() => setEditStage({ ...stage })}>
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteClick(stage)}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {addStageOpen ? (
                <div className="p-4 rounded-lg border bg-slate-50 space-y-3">
                    <Label>New Stage</Label>
                    <Input value={newStage.name} onChange={(e) => setNewStage({ ...newStage, name: e.target.value })} placeholder="Stage name" className="rounded-lg" />
                    <div className="flex gap-2">
                        <Input type="color" value={newStage.color} onChange={(e) => setNewStage({ ...newStage, color: e.target.value })} className="w-12 h-10 p-1 cursor-pointer" />
                        <Select value={newStage.category} onValueChange={(v) => setNewStage({ ...newStage, category: v })}>
                            <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="OPEN">OPEN</SelectItem>
                                <SelectItem value="WON">WON</SelectItem>
                                <SelectItem value="LOST">LOST</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setAddStageOpen(false)}>Cancel</Button>
                        <Button size="sm" onClick={handleAddStage}>Add</Button>
                    </div>
                </div>
            ) : (
                <Button variant="outline" className="w-full" onClick={() => setAddStageOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Stage
                </Button>
            )}

            {editStage && (
                <Dialog open={!!editStage} onOpenChange={() => setEditStage(null)}>
                    <DialogContent className="rounded-2xl max-w-md">
                        <DialogHeader><DialogTitle>Edit Stage</DialogTitle></DialogHeader>
                        <div className="space-y-3 mt-4">
                            <Label>Name</Label>
                            <Input value={editStage.name} onChange={(e) => setEditStage({ ...editStage, name: e.target.value })} className="rounded-lg" />
                            <div className="flex gap-2 items-center">
                                <Label>Color</Label>
                                <Input type="color" value={editStage.color} onChange={(e) => setEditStage({ ...editStage, color: e.target.value })} className="w-12 h-10 p-1 cursor-pointer" />
                            </div>
                            <Select value={editStage.category} onValueChange={(v) => setEditStage({ ...editStage, category: v })}>
                                <SelectTrigger className="rounded-lg"><SelectValue placeholder="Category" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="OPEN">OPEN</SelectItem>
                                    <SelectItem value="WON">WON</SelectItem>
                                    <SelectItem value="LOST">LOST</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="flex gap-2 pt-2">
                                <Button variant="outline" onClick={() => setEditStage(null)}>Cancel</Button>
                                <Button onClick={handleSaveEdit}>Save</Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            <AlertDialog open={!!deleteStage} onOpenChange={() => setDeleteStage(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Leads exist in this stage</AlertDialogTitle>
                        <AlertDialogDescription>
                            There are {leadCount} lead{leadCount !== 1 ? 's' : ''} in &quot;{deleteStage?.name}&quot;. You must move all leads to another stage before this stage can be deleted. Select a target stage below:
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-4">
                        <Select value={migrateTargetId} onValueChange={setMigrateTargetId}>
                            <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select target stage" /></SelectTrigger>
                            <SelectContent>
                                {localStages.filter(s => s.id !== deleteStage?.id).map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleMigrateAndDelete} disabled={!migrateTargetId}>
                            Move & Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

const LEADS_PER_PAGE = 10;

const LoadMoreSentinel = ({ stageId, onLoadMore, hasMore, loading }) => {
    const sentinelRef = useRef(null);
    const onLoadMoreRef = useRef(onLoadMore);
    onLoadMoreRef.current = onLoadMore;

    useEffect(() => {
        if (!hasMore || loading) return;
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) onLoadMoreRef.current(stageId);
            },
            { rootMargin: '100px', threshold: 0.1 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [stageId, hasMore, loading]);

    if (!hasMore) return null;
    return (
        <div ref={sentinelRef} className="flex justify-center py-3 min-h-[40px]">
            {loading && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
        </div>
    );
};

const PipelinePage = () => {
    const { user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [stages, setStages] = useState([]);
    const [leadsByStage, setLeadsByStage] = useState({});
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [pipelineConfigOpen, setPipelineConfigOpen] = useState(false);
    const [newLead, setNewLead] = useState({
        name: '',
        email: '',
        company: '',
        source: 'Cold Outbound',
        job_title: '',
        website: '',
        phone_number: '',
        linkedin_profile: '',
        estimated_value: '',
        initial_notes: '',
        status: null,
        tags: []
    });
    const [formErrors, setFormErrors] = useState({});
    const [filterMode, setFilterMode] = useState(() => sessionStorage.getItem('pipeline_filterMode') || 'all');
    const [teamMembers, setTeamMembers] = useState([]);
    const [tags, setTags] = useState([]);
    const [selectedTagFilters, setSelectedTagFilters] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem('pipeline_selectedTagFilters') || '[]'); } catch { return []; }
    });
    const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem('pipeline_searchQuery') || '');
    const [sortOrder, setSortOrder] = useState(() => sessionStorage.getItem('pipeline_sortOrder') || 'none'); // 'none' | 'newest' | 'oldest'
    const [pendingMove, setPendingMove] = useState(null); // { leadId, leadName, sourceStageId, destStageId, newStatus }
    const [logInteractionOpen, setLogInteractionOpen] = useState(false);

    useEffect(() => { sessionStorage.setItem('pipeline_filterMode', filterMode); }, [filterMode]);
    useEffect(() => { sessionStorage.setItem('pipeline_selectedTagFilters', JSON.stringify(selectedTagFilters)); }, [selectedTagFilters]);
    useEffect(() => { sessionStorage.setItem('pipeline_searchQuery', searchQuery); }, [searchQuery]);
    useEffect(() => { sessionStorage.setItem('pipeline_sortOrder', sortOrder); }, [sortOrder]);

    useEffect(() => {
        fetchTeamMembers();
        fetchTags();
    }, []);

    useEffect(() => {
        fetchPipeline();
    }, [filterMode, selectedTagFilters]);

    // Debounce search — wait 400ms after user stops typing before re-fetching
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchPipeline(searchQuery);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const fetchTags = async () => {
        try {
            const response = await axios.get(`${API_URL}/tags`);
            setTags(response.data);
        } catch (error) {
            console.error('Failed to fetch tags:', error);
        }
    };

    const fetchTeamMembers = async () => {
        try {
            const response = await axios.get(`${API_URL}/team/members`);
            setTeamMembers(response.data);
        } catch (error) {
            console.error('Failed to fetch team members:', error);
        }
    };

    const fetchPipeline = async (search = searchQuery) => {
        try {
            const params = new URLSearchParams();
            params.append('per_stage_limit', LEADS_PER_PAGE);
            if (filterMode !== 'all') params.append('filter', filterMode);
            if (selectedTagFilters.length > 0) params.append('tags', selectedTagFilters.join(','));
            if (search && search.trim()) params.append('search', search.trim());
            const url = `${API_URL}/pipeline${params.toString() ? '?' + params.toString() : ''}`;
            const response = await axios.get(url);
            setStages(response.data.stages || []);
            setLeadsByStage(response.data.leads_by_stage || {});
        } catch (error) {
            toast.error('Failed to fetch pipeline');
        }
    };

    const loadingStagesRef = useRef(new Set());
    const leadsByStageRef = useRef(leadsByStage);
    useEffect(() => {
        leadsByStageRef.current = leadsByStage;
    }, [leadsByStage]);

    const fetchMoreLeads = useCallback(async (stageId) => {
        if (searchQuery.trim()) return; // server already returned full results when searching
        if (loadingStagesRef.current.has(stageId)) return;
        const stageData = leadsByStageRef.current[stageId];
        if (!stageData?.has_more || stageData?.loading) return;
        loadingStagesRef.current.add(stageId);
        setLeadsByStage(prev => ({ ...prev, [stageId]: { ...prev[stageId], loading: true } }));
        try {
            const params = new URLSearchParams();
            params.append('limit', LEADS_PER_PAGE);
            params.append('offset', (stageData.leads || []).length);
            if (filterMode !== 'all') params.append('filter', filterMode);
            if (selectedTagFilters.length > 0) params.append('tags', selectedTagFilters.join(','));
            const { data } = await axios.get(`${API_URL}/pipeline/stages/${stageId}/leads?${params}`);
            setLeadsByStage(prev => ({
                ...prev,
                [stageId]: {
                    leads: [...(prev[stageId]?.leads || []), ...(data.leads || [])],
                    total: data.total ?? prev[stageId]?.total,
                    has_more: data.has_more ?? false,
                    loading: false
                }
            }));
        } catch (error) {
            toast.error('Failed to load more leads');
            setLeadsByStage(prev => ({ ...prev, [stageId]: { ...prev[stageId], loading: false } }));
        } finally {
            loadingStagesRef.current.delete(stageId);
        }
    }, [filterMode, selectedTagFilters]);


    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateForm = () => {
        const errors = {};

        if (!newLead.name || newLead.name.trim() === '') {
            errors.name = 'Full Name is required';
        }

        if (!newLead.email || newLead.email.trim() === '') {
            errors.email = 'Email is required';
        } else if (!validateEmail(newLead.email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!newLead.company || newLead.company.trim() === '') {
            errors.company = 'Company Name is required';
        }

        if (!newLead.source) {
            errors.source = 'Source is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const createLead = async () => {
        if (!validateForm()) {
            return;
        }

        const targetStageId = newLead.status || stages[0]?.id;
        try {
            const { data } = await axios.post(`${API_URL}/leads`, newLead);
            toast.success('Lead created successfully');
            setCreateDialogOpen(false);
            setNewLead({
                name: '',
                email: '',
                company: '',
                source: 'Cold Outbound',
                job_title: '',
                website: '',
                phone_number: '',
                linkedin_profile: '',
                estimated_value: '',
                initial_notes: '',
                status: stages[0]?.id || null,
                tags: []
            });
            setFormErrors({});
            if (targetStageId && data) {
                setLeadsByStage(prev => ({
                    ...prev,
                    [targetStageId]: {
                        ...prev[targetStageId],
                        leads: [data, ...(prev[targetStageId]?.leads || [])],
                        total: (prev[targetStageId]?.total ?? 0) + 1
                    }
                }));
            } else {
                fetchPipeline();
            }
        } catch (error) {
            const errMsg = getApiError(error, 'Failed to create lead');
            if (error.response?.status === 400 && errMsg.includes('already exists')) {
                setFormErrors({ email: 'Lead with this email already exists' });
            } else {
                toast.error(errMsg);
            }
        }
    };

    const updateLeadInStage = (stageId, leadId, updates) => {
        setLeadsByStage(prev => {
            const stageData = prev[stageId];
            if (!stageData?.leads) return prev;
            return {
                ...prev,
                [stageId]: {
                    ...stageData,
                    leads: stageData.leads.map(l => l.id === leadId ? { ...l, ...updates } : l)
                }
            };
        });
    };

    const claimLead = async (leadId) => {
        const stageId = Object.keys(leadsByStage).find(sid => leadsByStage[sid]?.leads?.some(l => l.id === leadId));
        try {
            const { data } = await axios.post(`${API_URL}/leads/${leadId}/claim`);
            toast.success('Lead claimed successfully');
            if (stageId) updateLeadInStage(stageId, leadId, { owner_id: data?.owner_id ?? user?.id });
        } catch (error) {
            toast.error(getApiError(error, 'Failed to claim lead'));
        }
    };

    const assignLead = async (leadId, ownerId) => {
        const stageId = Object.keys(leadsByStage).find(sid => leadsByStage[sid]?.leads?.some(l => l.id === leadId));
        try {
            await axios.post(`${API_URL}/leads/${leadId}/assign`, { owner_id: ownerId });
            toast.success('Lead assigned successfully');
            if (stageId) updateLeadInStage(stageId, leadId, { owner_id: ownerId });
        } catch (error) {
            toast.error(getApiError(error, 'Failed to assign lead'));
        }
    };

    const getOwnerInfo = (ownerId) => {
        if (!ownerId) return null;
        const owner = teamMembers.find(m => m.id === ownerId);
        return owner;
    };

    const getInitials = (email) => {
        if (!email) return '?';
        // Try to extract meaningful initials from email username
        const username = email.split('@')[0];

        // Check if username contains dots or underscores (e.g., john.doe, jane_smith)
        if (username.includes('.')) {
            const parts = username.split('.').filter(p => p.length > 0);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            } else if (parts.length === 1 && parts[0].length >= 2) {
                return parts[0].substring(0, 2).toUpperCase();
            }
        } else if (username.includes('_')) {
            const parts = username.split('_').filter(p => p.length > 0);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            } else if (parts.length === 1 && parts[0].length >= 2) {
                return parts[0].substring(0, 2).toUpperCase();
            }
        }

        // Default: first two characters
        return username.length >= 2 ? username.substring(0, 2).toUpperCase() : username.toUpperCase() + '?';
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const { source, destination, draggableId } = result;

        if (source.droppableId === destination.droppableId) return;

        const newStatus = destination.droppableId;
        const sourceStageId = source.droppableId;
        const destStageId = destination.droppableId;

        const sourceLeads = getLeadsByStatus(sourceStageId);
        const lead = sourceLeads.find(l => l.id === draggableId);
        if (!lead) return;

        // Optimistically move the card
        const updatedLead = { ...lead, status: newStatus };
        setLeadsByStage(prev => ({
            ...prev,
            [sourceStageId]: {
                ...prev[sourceStageId],
                leads: (prev[sourceStageId]?.leads || []).filter(l => l.id !== draggableId),
                total: Math.max(0, (prev[sourceStageId]?.total ?? 0) - 1)
            },
            [destStageId]: {
                ...prev[destStageId],
                leads: [updatedLead, ...(prev[destStageId]?.leads || [])],
                total: (prev[destStageId]?.total ?? 0) + 1
            }
        }));

        // Store pending move and open log interaction modal
        setPendingMove({ lead, leadId: draggableId, leadName: lead.name, sourceStageId, destStageId, newStatus });
        setLogInteractionOpen(true);
    };

    const confirmPendingMove = async () => {
        if (!pendingMove) return;
        try {
            await axios.patch(`${API_URL}/leads/${pendingMove.leadId}`, { status: pendingMove.newStatus });
            toast.success('Lead moved & interaction logged');
        } catch (error) {
            toast.error('Failed to update lead status');
            // Revert optimistic update
            revertPendingMove();
        }
        setPendingMove(null);
    };

    const revertPendingMove = () => {
        if (!pendingMove) return;
        const { lead, leadId, sourceStageId, destStageId } = pendingMove;
        setLeadsByStage(prev => ({
            ...prev,
            [destStageId]: {
                ...prev[destStageId],
                leads: (prev[destStageId]?.leads || []).filter(l => l.id !== leadId),
                total: Math.max(0, (prev[destStageId]?.total ?? 0) - 1)
            },
            [sourceStageId]: {
                ...prev[sourceStageId],
                leads: [lead, ...(prev[sourceStageId]?.leads || [])],
                total: (prev[sourceStageId]?.total ?? 0) + 1
            }
        }));
    };

    const handleLogInteractionClose = () => {
        setLogInteractionOpen(false);
        // If closed without saving, revert the move
        revertPendingMove();
        setPendingMove(null);
    };

    const handleLogInteractionSaved = () => {
        setLogInteractionOpen(false);
        confirmPendingMove();
    };

    const openLeadDetail = (lead) => {
        navigate(`/leads/${lead.id}`);
    };

    const getLeadsByStatus = (stageId) => {
        let leads = leadsByStage[stageId]?.leads || [];

        if (sortOrder === 'newest') {
            leads = [...leads].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
        } else if (sortOrder === 'oldest') {
            leads = [...leads].sort((a, b) => new Date(a.updated_at || 0) - new Date(b.updated_at || 0));
        }

        return leads;
    };

    const getStageTotal = (stageId) => leadsByStage[stageId]?.total ?? 0;
    const getStageHasMore = (stageId) => leadsByStage[stageId]?.has_more ?? false;
    const getStageLoading = (stageId) => leadsByStage[stageId]?.loading ?? false;

    const getStageStyle = (stage) => {
        const color = stage?.color || '#94a3b8';
        return { borderColor: color + '80', backgroundColor: color + '15' };
    };

    const getSentimentColor = (sentiment) => {
        switch (sentiment) {
            case 'Positive':
                return 'text-green-600 bg-green-50';
            case 'Negative':
                return 'text-red-600 bg-red-50';
            case 'Neutral':
                return 'text-slate-600 bg-slate-50';
            default:
                return 'text-slate-600 bg-slate-50';
        }
    };

    return (
        <div className="h-[calc(100vh-4rem)] bg-slate-50/50" data-testid="pipeline-page">
            <div className="p-6 border-b border-slate-200 bg-white">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Pipeline</h1>
                        <p className="text-sm leading-relaxed text-slate-600">Manage your leads through the sales pipeline</p>
                    </div>
                    <div className="flex gap-2">
                        {isAdmin() && (
                            <Dialog open={pipelineConfigOpen} onOpenChange={setPipelineConfigOpen}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" className="rounded-lg font-medium">
                                        <Settings2 className="h-4 w-4 mr-2" />
                                        Pipeline Configuration
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                        <DialogTitle className="text-2xl font-semibold text-slate-900">Pipeline Settings</DialogTitle>
                                    </DialogHeader>
                                    <PipelineConfigModal
                                        stages={stages}
                                        onClose={() => setPipelineConfigOpen(false)}
                                        onSaved={() => { setPipelineConfigOpen(false); fetchPipeline(); }}
                                    />
                                </DialogContent>
                            </Dialog>
                        )}
                        <Dialog open={createDialogOpen} onOpenChange={(open) => {
                            setCreateDialogOpen(open);
                            if (open && stages.length > 0) {
                                setNewLead(prev => ({ ...prev, status: prev.status || stages[0]?.id }));
                            }
                        }}>
                            <DialogTrigger asChild>
                                <Button
                                    data-testid="create-lead-button"
                                    className="rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    New Lead
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="text-2xl font-semibold text-slate-900">Create New Lead</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-6 mt-4">
                                    {/* Essential Info Section */}
                                    <div className="space-y-4">
                                        <div className="border-b border-slate-200 pb-2">
                                            <h3 className="text-lg font-semibold text-slate-900">Essential Info</h3>
                                            <p className="text-sm text-slate-600">Required fields to create a lead</p>
                                        </div>

                                        <div>
                                            <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="name"
                                                data-testid="lead-name-input"
                                                value={newLead.name}
                                                onChange={(e) => {
                                                    setNewLead({ ...newLead, name: e.target.value });
                                                    if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                                                }}
                                                placeholder="John Doe"
                                                className={`rounded-lg ${formErrors.name ? 'border-red-500' : ''}`}
                                            />
                                            {formErrors.name && <p className="text-sm text-red-500 mt-1">{formErrors.name}</p>}
                                        </div>

                                        <div>
                                            <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="email"
                                                data-testid="lead-email-input"
                                                type="email"
                                                value={newLead.email}
                                                onChange={(e) => {
                                                    setNewLead({ ...newLead, email: e.target.value });
                                                    if (formErrors.email) setFormErrors({ ...formErrors, email: '' });
                                                }}
                                                placeholder="john@example.com"
                                                className={`rounded-lg ${formErrors.email ? 'border-red-500' : ''}`}
                                            />
                                            {formErrors.email && <p className="text-sm text-red-500 mt-1">{formErrors.email}</p>}
                                        </div>

                                        <div>
                                            <Label htmlFor="company">Company Name <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="company"
                                                data-testid="lead-company-input"
                                                value={newLead.company}
                                                onChange={(e) => {
                                                    setNewLead({ ...newLead, company: e.target.value });
                                                    if (formErrors.company) setFormErrors({ ...formErrors, company: '' });
                                                }}
                                                placeholder="Acme Corp"
                                                className={`rounded-lg ${formErrors.company ? 'border-red-500' : ''}`}
                                            />
                                            {formErrors.company && <p className="text-sm text-red-500 mt-1">{formErrors.company}</p>}
                                        </div>

                                        <div>
                                            <Label htmlFor="source">Source <span className="text-red-500">*</span></Label>
                                            <Select
                                                value={newLead.source}
                                                onValueChange={(value) => {
                                                    setNewLead({ ...newLead, source: value });
                                                    if (formErrors.source) setFormErrors({ ...formErrors, source: '' });
                                                }}
                                            >
                                                <SelectTrigger className={`rounded-lg ${formErrors.source ? 'border-red-500' : ''}`}>
                                                    <SelectValue placeholder="Select source" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Cold Outbound">Cold Outbound</SelectItem>
                                                    <SelectItem value="Email Campaign">Email Campaign</SelectItem>
                                                    <SelectItem value="Website">Website</SelectItem>
                                                    <SelectItem value="Referral">Referral</SelectItem>
                                                    <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                                                    <SelectItem value="Other">Other</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            {formErrors.source && <p className="text-sm text-red-500 mt-1">{formErrors.source}</p>}
                                        </div>
                                    </div>

                                    {/* Enrichment Section */}
                                    <div className="space-y-4">
                                        <div className="border-b border-slate-200 pb-2">
                                            <h3 className="text-lg font-semibold text-slate-900">Enrichment</h3>
                                            <p className="text-sm text-slate-600">Optional fields to enrich lead profile</p>
                                        </div>

                                        <div>
                                            <Label htmlFor="job_title">Job Title</Label>
                                            <Input
                                                id="job_title"
                                                value={newLead.job_title}
                                                onChange={(e) => setNewLead({ ...newLead, job_title: e.target.value })}
                                                placeholder="CTO"
                                                className="rounded-lg"
                                            />
                                        </div>

                                        <div>
                                            <Label htmlFor="website">Website</Label>
                                            <Input
                                                id="website"
                                                type="url"
                                                value={newLead.website}
                                                onChange={(e) => setNewLead({ ...newLead, website: e.target.value })}
                                                placeholder="https://example.com"
                                                className="rounded-lg"
                                            />
                                        </div>

                                        <div>
                                            <Label htmlFor="phone_number">Phone Number</Label>
                                            <Input
                                                id="phone_number"
                                                type="tel"
                                                value={newLead.phone_number}
                                                onChange={(e) => setNewLead({ ...newLead, phone_number: e.target.value })}
                                                placeholder="+1 (555) 123-4567"
                                                className="rounded-lg"
                                            />
                                        </div>

                                        <div>
                                            <Label htmlFor="linkedin_profile">LinkedIn Profile</Label>
                                            <Input
                                                id="linkedin_profile"
                                                type="url"
                                                value={newLead.linkedin_profile}
                                                onChange={(e) => setNewLead({ ...newLead, linkedin_profile: e.target.value })}
                                                placeholder="https://linkedin.com/in/johndoe"
                                                className="rounded-lg"
                                            />
                                        </div>

                                        <div>
                                            <Label htmlFor="estimated_value">Estimated Value</Label>
                                            <Input
                                                id="estimated_value"
                                                value={newLead.estimated_value}
                                                onChange={(e) => setNewLead({ ...newLead, estimated_value: e.target.value })}
                                                placeholder="$10,000"
                                                className="rounded-lg"
                                            />
                                        </div>

                                        <div>
                                            <Label htmlFor="initial_notes">Initial Notes</Label>
                                            <Textarea
                                                id="initial_notes"
                                                value={newLead.initial_notes}
                                                onChange={(e) => setNewLead({ ...newLead, initial_notes: e.target.value })}
                                                placeholder="Add any initial notes about this lead..."
                                                className="rounded-lg min-h-[100px]"
                                            />
                                        </div>

                                        <div>
                                            <Label htmlFor="tags">Tags</Label>
                                            <TagSelect
                                                value={newLead.tags || []}
                                                onChange={(tags) => setNewLead({ ...newLead, tags })}
                                                placeholder="Select or create tags..."
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-3 pt-4">
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setCreateDialogOpen(false);
                                                setFormErrors({});
                                            }}
                                            className="flex-1 rounded-lg"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            data-testid="submit-lead-button"
                                            onClick={createLead}
                                            className="flex-1 rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                        >
                                            Save Lead
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* Search + Sort */}
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                        <input
                            type="text"
                            aria-label="Search by name or email"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search by name or email…"
                            className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 w-64"
                        />
                        {searchQuery && (
                            <button type="button" aria-label="Clear search" onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                <X className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                    <Select value={sortOrder} onValueChange={setSortOrder}>
                        <SelectTrigger className="w-[180px] rounded-lg bg-white">
                            <ArrowUpDown className="h-3.5 w-3.5 mr-2 text-slate-400" />
                            <SelectValue placeholder="Sort by…" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">Default order</SelectItem>
                            <SelectItem value="newest">Last updated (newest)</SelectItem>
                            <SelectItem value="oldest">Last updated (oldest)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Filter Toggle Group */}
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Label className="text-sm font-medium text-slate-700">View:</Label>
                        <ToggleGroup
                            type="single"
                            value={filterMode}
                            onValueChange={(value) => value && setFilterMode(value)}
                            className="bg-slate-100 rounded-lg p-1"
                        >
                            <ToggleGroupItem
                                value="mine-unassigned"
                                aria-label="Mine and Unassigned"
                                className="data-[state=on]:bg-white data-[state=on]:shadow-sm rounded-md px-3 py-1.5 text-sm"
                            >
                                Mine + Unassigned
                            </ToggleGroupItem>
                            <ToggleGroupItem
                                value="mine"
                                aria-label="Mine only"
                                className="data-[state=on]:bg-white data-[state=on]:shadow-sm rounded-md px-3 py-1.5 text-sm"
                            >
                                Mine
                            </ToggleGroupItem>
                            <ToggleGroupItem
                                value="unassigned"
                                aria-label="Unassigned only"
                                className="data-[state=on]:bg-white data-[state=on]:shadow-sm rounded-md px-3 py-1.5 text-sm"
                            >
                                Unassigned
                            </ToggleGroupItem>
                            <ToggleGroupItem
                                value="all"
                                aria-label="All leads"
                                className="data-[state=on]:bg-white data-[state=on]:shadow-sm rounded-md px-3 py-1.5 text-sm"
                            >
                                All
                            </ToggleGroupItem>
                        </ToggleGroup>
                    </div>

                    {/* Tag Filter */}
                    <div className="flex items-center gap-3">
                        <Label className="text-sm font-medium text-slate-700">Tags:</Label>
                        <Select
                            value=""
                            onValueChange={(value) => {
                                if (value && !selectedTagFilters.includes(value)) {
                                    setSelectedTagFilters([...selectedTagFilters, value]);
                                }
                            }}
                            disabled={tags.length === 0}
                        >
                            <SelectTrigger className="w-[200px] rounded-lg bg-white">
                                <SelectValue placeholder="Filter by tags..." />
                            </SelectTrigger>
                            <SelectContent>
                                {tags
                                    .filter(tag => tag && !selectedTagFilters.includes(tag.id))
                                    .map((tag) => (
                                        <SelectItem key={tag.id} value={tag.id}>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-3 h-3 rounded-full"
                                                    style={{ backgroundColor: tag.color }}
                                                />
                                                {tag.name}
                                            </div>
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                        {selectedTagFilters.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                                {selectedTagFilters.map((tagId) => {
                                    const tag = tags.find(t => t && t.id === tagId);
                                    if (!tag) return null;
                                    const bgColor = tag.color ? tag.color + '20' : '#e5e7eb';
                                    const textColor = tag.color || '#374151';
                                    return (
                                        <Badge
                                            key={tagId}
                                            variant="secondary"
                                            className="text-xs px-2 py-0.5 rounded-md font-medium cursor-pointer"
                                            style={{
                                                backgroundColor: bgColor,
                                                color: textColor,
                                                borderColor: textColor + '40'
                                            }}
                                            onClick={() => setSelectedTagFilters(selectedTagFilters.filter(id => id !== tagId))}
                                        >
                                            {tag.name}
                                            <X className="h-3 w-3 ml-1.5" />
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="flex h-full overflow-x-auto gap-6 p-6 min-w-0">
                    {stages.map((stage) => (
                        <Droppable key={stage.id} droppableId={stage.id}>
                            {(provided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className="min-w-[320px] max-w-[320px] flex-shrink-0 flex flex-col h-full rounded-xl border"
                                    style={getStageStyle(stage)}
                                    data-testid={`column-${stage.name.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    <div className="p-4 border-b border-slate-200/60 bg-white/80 backdrop-blur-sm rounded-t-xl">
                                        <h3 className="text-xl font-medium text-slate-900">{stage.name}</h3>
                                        <p className="text-xs text-slate-600 mt-1">{getStageTotal(stage.id)} leads</p>
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                        {getLeadsByStatus(stage.id).map((lead, index) => (
                                            <Draggable key={lead.id} draggableId={lead.id} index={index}>
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        onClick={() => openLeadDetail(lead)}
                                                        className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md cursor-pointer transition-all group relative"
                                                        data-testid={`lead-card-${lead.id}`}
                                                    >
                                                        <div className="flex items-start gap-3 mb-2">
                                                            {lead.logo_url && (
                                                                <img
                                                                    src={lead.logo_url}
                                                                    alt={lead.company || 'Company logo'}
                                                                    className="w-10 h-10 rounded-lg object-contain border border-slate-200 flex-shrink-0"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                            )}
                                                            {!lead.logo_url && lead.company && (
                                                                <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                                                                    <Building2 className="h-5 w-5 text-slate-400" />
                                                                </div>
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-1.5 mb-1">
                                                                    <p className="font-semibold text-slate-900 truncate">{lead.name}</p>
                                                                    {/* Story 14.4: Fire emoji for hot leads */}
                                                                    {lead.score !== undefined && lead.score !== null && lead.score > 10 && (
                                                                        <span className="text-lg" title={`Hot Lead - ${lead.score} points`}>🔥</span>
                                                                    )}
                                                                </div>
                                                                {lead.company && (
                                                                    <div className="flex items-center gap-2 text-sm text-slate-500">
                                                                        <Building2 className="h-3 w-3 flex-shrink-0" />
                                                                        <span className="truncate">{lead.company}</span>
                                                                    </div>
                                                                )}
                                                                {/* Story 14.4: Display lead score - show even if 0 */}
                                                                {lead.score !== undefined && lead.score !== null && (
                                                                    <div className="text-xs text-slate-500 mt-1">
                                                                        {lead.score} {lead.score === 1 ? 'pt' : 'pts'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                                                            <Mail className="h-3 w-3" />
                                                            <span className="truncate">{lead.email}</span>
                                                        </div>
                                                        {lead.sentiment_score && (
                                                            <div className="flex gap-2 mb-3">
                                                                <span
                                                                    className={`text-xs px-2 py-0.5 rounded-md font-medium ${getSentimentColor(
                                                                        lead.sentiment_score
                                                                    )}`}
                                                                >
                                                                    {lead.sentiment_score}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Tags Section */}
                                                        {lead.tags && lead.tags.length > 0 && tags.length > 0 && (() => {
                                                            const visibleTags = tags.filter(tag => tag && lead.tags.includes(tag.id));
                                                            return visibleTags.length > 0 && (
                                                                <div className="flex flex-wrap gap-1.5 mb-3">
                                                                    {visibleTags
                                                                        .slice(0, 2)
                                                                        .map((tag) => {
                                                                            const bgColor = tag.color ? tag.color + '20' : '#e5e7eb';
                                                                            const textColor = tag.color || '#374151';
                                                                            return (
                                                                                <Badge
                                                                                    key={tag.id}
                                                                                    variant="secondary"
                                                                                    className="text-xs px-1.5 py-0.5 rounded-md font-medium"
                                                                                    style={{
                                                                                        backgroundColor: bgColor,
                                                                                        color: textColor,
                                                                                        borderColor: textColor + '40'
                                                                                    }}
                                                                                >
                                                                                    {tag.name}
                                                                                </Badge>
                                                                            );
                                                                        })}
                                                                    {visibleTags.length > 2 && (
                                                                        <Badge
                                                                            variant="secondary"
                                                                            className="text-xs px-1.5 py-0.5 rounded-md font-medium bg-slate-100 text-slate-600"
                                                                        >
                                                                            +{visibleTags.length - 2}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Owner/Assignment Section */}
                                                        <div className="pt-3 border-t border-slate-100">
                                                            <div className="flex flex-col gap-1.5 min-w-0">
                                                                <span className="text-xs text-slate-400 whitespace-nowrap">
                                                                    {lead.updated_at
                                                                        ? format(new Date(lead.updated_at), 'MMM d, yyyy h:mm a')
                                                                        : 'N/A'}
                                                                </span>

                                                                {lead.owner_id ? (
                                                                    <div className="flex items-start gap-1.5 min-w-0">
                                                                        <Avatar className="h-6 w-6 flex-shrink-0 mt-0.5">
                                                                            <AvatarFallback className="text-xs bg-slate-200">
                                                                                {getInitials(getOwnerInfo(lead.owner_id)?.email || '')}
                                                                            </AvatarFallback>
                                                                        </Avatar>
                                                                        <div className="flex-1 min-w-0 relative">
                                                                            <Select
                                                                                value={lead.owner_id}
                                                                                onValueChange={(value) => {
                                                                                    assignLead(lead.id, value);
                                                                                }}
                                                                            >
                                                                                <SelectTrigger
                                                                                    className="h-auto border-0 bg-transparent p-0 text-xs hover:bg-slate-100 rounded focus:ring-0 min-w-0 w-full justify-start [&>span]:block [&>span]:break-words"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                >
                                                                                    <SelectValue>
                                                                                        <span className="text-xs text-slate-600 break-words">
                                                                                            {getOwnerInfo(lead.owner_id)?.email || 'Unknown'}
                                                                                        </span>
                                                                                    </SelectValue>
                                                                                </SelectTrigger>
                                                                                <SelectContent onClick={(e) => e.stopPropagation()}>
                                                                                    {teamMembers.map((member) => (
                                                                                        <SelectItem key={member.id} value={member.id} className="text-xs">
                                                                                            {member.email}
                                                                                        </SelectItem>
                                                                                    ))}
                                                                                </SelectContent>
                                                                            </Select>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        className="h-6 px-2 text-xs hover:bg-slate-100 w-fit"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            claimLead(lead.id);
                                                                        }}
                                                                    >
                                                                        <UserPlus className="h-3 w-3 mr-1" />
                                                                        Claim
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                        <LoadMoreSentinel
                                            stageId={stage.id}
                                            onLoadMore={fetchMoreLeads}
                                            hasMore={getStageHasMore(stage.id)}
                                            loading={getStageLoading(stage.id)}
                                        />
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    ))}
                </div>
            </DragDropContext>

            {/* Log Interaction Modal — shown when a card is moved between stages */}
            {pendingMove && (
                <LogInteractionModal
                    open={logInteractionOpen}
                    onClose={handleLogInteractionClose}
                    leadId={pendingMove.leadId}
                    leadName={pendingMove.leadName}
                    onSaved={handleLogInteractionSaved}
                    contextNote={`Moved to ${stages.find(s => s.id === pendingMove.destStageId)?.name || 'new stage'}`}
                />
            )}
        </div>
    );
};

export default PipelinePage;
